// SIMPLE EXCEL STORAGE - Direct AWS storage for Excel data
import awsS3Service from './awsS3Service';

export interface SimpleExcelData {
  id: string;
  headers: string[];
  rows: string[][];
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  assignedTo?: string[]; // Users who should see this data
}

class SimpleExcelStorage {
  private static instance: SimpleExcelStorage;
  private readonly S3_BUCKET = 'ettaskmanager';
  private readonly S3_PREFIX = 'simple-excel/';

  static getInstance(): SimpleExcelStorage {
    if (!SimpleExcelStorage.instance) {
      SimpleExcelStorage.instance = new SimpleExcelStorage();
    }
    return SimpleExcelStorage.instance;
  }

  /**
   * Save Excel data to AWS S3 - SIMPLE METHOD
   */
  async saveExcelData(excelData: SimpleExcelData): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('💾 SimpleExcelStorage: Saving Excel data to AWS S3...', excelData);

      // Save to AWS S3
      const s3Key = `${this.S3_PREFIX}${excelData.id}.json`;
      await awsS3Service.uploadStringContent(
        this.S3_BUCKET,
        s3Key,
        JSON.stringify(excelData, null, 2),
        'application/json'
      );

      console.log('✅ SimpleExcelStorage: Excel data saved to AWS S3 successfully');

      // Also save to localStorage as backup
      localStorage.setItem(`simple_excel_${excelData.id}`, JSON.stringify(excelData));

      return { success: true };
    } catch (error) {
      console.error('❌ SimpleExcelStorage: Error saving to AWS S3:', error);
      return { success: false, error: error instanceof Error ? error.message : String(error) };
    }
  }

  /**
   * Get Excel data for a specific user - SIMPLE METHOD
   */
  async getUserExcelData(userId: string): Promise<SimpleExcelData[]> {
    try {
      console.log('📥 SimpleExcelStorage: Getting Excel data for user:', userId);

      // First try to get from AWS S3
      const s3Key = `${this.S3_PREFIX}`;
      const files = await awsS3Service.listFiles(this.S3_BUCKET, s3Key);
      
      console.log(`📥 SimpleExcelStorage: Found ${files.length} Excel files in AWS S3`);

      const userExcelData: SimpleExcelData[] = [];

      // Load each Excel file
      for (const file of files) {
        try {
          const fileContent = await awsS3Service.downloadFile(this.S3_BUCKET, file.Key);
          const excelData: SimpleExcelData = JSON.parse(fileContent);

          // Check if this Excel data is assigned to the user
          if (this.isAssignedToUser(excelData, userId)) {
            userExcelData.push(excelData);
            console.log('✅ SimpleExcelStorage: Found Excel data assigned to user:', excelData.id);
          }
        } catch (error) {
          console.error('❌ SimpleExcelStorage: Error loading file:', file.Key, error);
        }
      }

      // Fallback to localStorage if AWS fails
      if (userExcelData.length === 0) {
        console.log('📥 SimpleExcelStorage: No AWS data found, checking localStorage...');
        const localData = this.getLocalStorageData(userId);
        userExcelData.push(...localData);
      }

      console.log(`📥 SimpleExcelStorage: Found ${userExcelData.length} Excel files for user`);
      return userExcelData;

    } catch (error) {
      console.error('❌ SimpleExcelStorage: Error getting user Excel data:', error);
      
      // Fallback to localStorage
      console.log('📥 SimpleExcelStorage: Falling back to localStorage...');
      return this.getLocalStorageData(userId);
    }
  }

  /**
   * Check if Excel data is assigned to a user - SIMPLE METHOD
   */
  private isAssignedToUser(excelData: SimpleExcelData, userId: string): boolean {
    // Check assignedTo array
    if (excelData.assignedTo && excelData.assignedTo.includes(userId)) {
      return true;
    }

    // Check if user is mentioned in any cell (simple text matching)
    const userLower = userId.toLowerCase();
    return excelData.rows.some(row => 
      row.some(cell => 
        cell.toLowerCase().includes(userLower) ||
        cell.toLowerCase().includes(userId.toLowerCase())
      )
    );
  }

  /**
   * Get data from localStorage as fallback
   */
  private getLocalStorageData(userId: string): SimpleExcelData[] {
    try {
      const keys = Object.keys(localStorage).filter(key => key.startsWith('simple_excel_'));
      const excelFiles = keys.map(key => {
        const data = localStorage.getItem(key);
        return data ? JSON.parse(data) : null;
      }).filter(Boolean) as SimpleExcelData[];

      return excelFiles.filter(excelData => this.isAssignedToUser(excelData, userId));
    } catch (error) {
      console.error('❌ SimpleExcelStorage: Error reading localStorage:', error);
      return [];
    }
  }

  /**
   * Create new Excel data with proper structure
   */
  createNewExcelData(headers: string[], createdBy: string): SimpleExcelData {
    return {
      id: `excel_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      headers,
      rows: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy,
      assignedTo: []
    };
  }

  /**
   * Add a row to Excel data
   */
  addRowToExcelData(excelData: SimpleExcelData, rowData: string[]): SimpleExcelData {
    const updatedData = {
      ...excelData,
      rows: [...excelData.rows, rowData],
      updatedAt: new Date().toISOString()
    };

    return updatedData;
  }

  /**
   * Update a cell in Excel data
   */
  updateCellInExcelData(
    excelData: SimpleExcelData, 
    rowIndex: number, 
    colIndex: number, 
    value: string
  ): SimpleExcelData {
    const updatedRows = [...excelData.rows];
    
    // Ensure row exists
    if (!updatedRows[rowIndex]) {
      updatedRows[rowIndex] = Array(excelData.headers.length).fill('');
    }
    
    // Update cell
    updatedRows[rowIndex][colIndex] = value;

    return {
      ...excelData,
      rows: updatedRows,
      updatedAt: new Date().toISOString()
    };
  }
}

export const simpleExcelStorage = SimpleExcelStorage.getInstance();








